import React, { useState, useEffect } from "react";
import "../styles/newCoupon.css";
import { supabase } from "../../supabase";
import { FiGift, FiPlus, FiTrendingUp, FiUsers, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import toast, { Toaster } from 'react-hot-toast';

const WEEKDAY_OPTIONS = [
  { value: 'monday', label: 'Mandag' },
  { value: 'tuesday', label: 'Tirsdag' },
  { value: 'wednesday', label: 'Onsdag' },
  { value: 'thursday', label: 'Torsdag' },
  { value: 'friday', label: 'Fredag' },
  { value: 'saturday', label: 'Lordag' },
  { value: 'sunday', label: 'Sondag' },
];

// Rewards (pointCost > 0) are managed on the separate /new-reward page.
const isReward = (c) => Number(c.pointCost) > 0;

// Maps a coupons_catalog row (snake_case) to the camelCase shape this page
// already renders (matches the old Firestore myCouponsFb doc).
function toCatalogShape(row) {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    pointCost: row.point_cost,
    unlimited: row.unlimited,
    expiration: row.expiration,
    maxUses: row.max_uses,
    repeatWeekly: row.repeat_weekly,
    repeatDays: row.repeat_days,
    priceBadge: row.price_badge,
    startTime: row.start_time,
    endTime: row.end_time,
    deleted: row.deleted,
    createdAt: row.created_at,
  };
}

function NewCoupon() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    priceBadge: "",
    startTime: "",
    endTime: "",
    expiration: "",
    unlimited: false,
    maxUses: 1,
    repeatWeekly: false,
    repeatDays: [],
  });
  const [couponStatsRows, setCouponStatsRows] = useState([]);
  const [allTimeCouponsUsed, setAllTimeCouponsUsed] = useState(0);

  // Coupons are soft-deleted (never actually removed), so there's no separate
  // archive collection needed anymore - a deleted coupon's usage history is
  // still just a query away via coupon_usage_log. The all-time total is a
  // plain count() now too, not a hand-maintained counter doc to keep in sync.
  const refreshStats = async (showToast = false) => {
    try {
      const { data: catalogRows, error: catalogError } = await supabase.from('coupons_catalog').select('*');
      if (catalogError) throw catalogError;
      const nonRewardCoupons = (catalogRows || []).map(toCatalogShape).filter(c => !isReward(c));

      const { data: usageRows, error: usageError } = await supabase.from('coupon_usage_log').select('coupon_id');
      if (usageError) throw usageError;
      const usageCountByCoupon = {};
      (usageRows || []).forEach(r => { usageCountByCoupon[r.coupon_id] = (usageCountByCoupon[r.coupon_id] || 0) + 1; });

      const allRows = nonRewardCoupons
        .map(c => ({
          id: c.id,
          couponId: c.id,
          title: c.title || c.id,
          usedCount: usageCountByCoupon[c.id] || 0,
          isArchived: Boolean(c.deleted),
        }))
        .sort((a, b) => (a.isArchived === b.isArchived ? 0 : a.isArchived ? 1 : -1));

      setCouponStatsRows(allRows);
      setAllTimeCouponsUsed((usageRows || []).length);

      if (showToast) {
        toast.success('Statistikk oppdatert!', { duration: 2000, position: 'top-center' });
      }
    } catch (err) {
      if (showToast) {
        toast.error('Kunne ikke oppdatere statistikk', { duration: 3000, position: 'top-center' });
      }
    }
  };

  const handleRefreshStats = async () => {
    await refreshStats(true);
  };

  useEffect(() => {
    async function fetchCoupons() {
      const { data, error } = await supabase.from('coupons_catalog').select('*');
      if (error) { console.error('Failed to fetch coupons:', error); return; }
      const couponsList = (data || []).map(toCatalogShape).filter((coupon) => !coupon.deleted && !isReward(coupon));
      setCoupons(couponsList);
    }
    fetchCoupons();
  }, []);

  useEffect(() => {
    refreshStats(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === "unlimited") {
      setForm({
        ...form,
        unlimited: checked,
        expiration: checked ? "" : form.expiration,
      });
    } else if (name === 'repeatWeekly') {
      setForm({
        ...form,
        repeatWeekly: checked,
        repeatDays: checked ? form.repeatDays : [],
      });
    } else if (name === 'maxUses') {
      setForm({ ...form, maxUses: value });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const toggleRepeatDay = (dayValue) => {
    setForm((prev) => {
      const exists = prev.repeatDays.includes(dayValue);
      const nextRepeatDays = exists
        ? prev.repeatDays.filter((day) => day !== dayValue)
        : [...prev.repeatDays, dayValue];
      return { ...prev, repeatDays: nextRepeatDays };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || (!form.expiration && !form.unlimited)) {
      toast.error('Fyll inn tittel, beskrivelse og utløpsdato (eller velg ubegrenset).', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    if ((form.startTime && !form.endTime) || (!form.startTime && form.endTime)) {
      toast.error('Velg både fra- og til-tid, eller la begge stå tomme.', {
        duration: 2500,
        position: 'top-center',
      });
      return;
    }

    if (form.startTime && form.endTime && form.endTime <= form.startTime) {
      toast.error('Til-tid må være senere enn fra-tid.', {
        duration: 2500,
        position: 'top-center',
      });
      return;
    }

    if (form.repeatWeekly && form.repeatDays.length === 0) {
      toast.error('Velg minst en ukedag for gjentakende kupong', {
        duration: 2500,
        position: 'top-center',
      });
      return;
    }

    const normalizedMaxUses = Math.max(1, Number.parseInt(form.maxUses, 10) || 1);
    const normalizedRepeatDays = WEEKDAY_OPTIONS
      .map((day) => day.value)
      .filter((day) => form.repeatDays.includes(day));
    const normalizedPriceBadge = (form.priceBadge || '').trim();

    const newCoupon = {
      title: form.title,
      description: form.description,
      price_badge: normalizedPriceBadge,
      start_time: form.startTime || '',
      end_time: form.endTime || '',
      expiration: form.unlimited ? null : form.expiration,
      unlimited: form.unlimited,
      max_uses: normalizedMaxUses,
      repeat_weekly: Boolean(form.repeatWeekly),
      repeat_days: form.repeatWeekly ? normalizedRepeatDays : [],
      point_cost: 0,
    };

    try {
      const { data, error } = await supabase.from('coupons_catalog').insert(newCoupon).select().single();
      if (error) throw error;

      setCoupons([...coupons, toCatalogShape(data)]);
      setForm({
        title: "",
        description: "",
        priceBadge: "",
        startTime: "",
        endTime: "",
        expiration: "",
        unlimited: false,
        maxUses: 1,
        repeatWeekly: false,
        repeatDays: [],
      });

      toast.success('Kupong opprettet!', {
        duration: 3000,
        position: 'top-center',
      });
    } catch (error) {
      console.error("Error creating coupon: ", error);
      toast.error('Feil ved oppretting av kupong', {
        duration: 3000,
        position: 'top-center',
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Ingen utløpsdato";
    const [year, month, day] = dateStr.split("-");
    return `${day}.${month}.${year}`;
  };

  return (
    <>
      <Toaster />
      <AdminDashboardNav />
      <div className="coupon-container">
        {/* Header */}
        <div className="coupon-header">
          <div>
            <h1><FiGift style={{ marginRight: '12px', verticalAlign: 'middle' }} />Kupongadministrasjon</h1>
            <p className="coupon-subtitle">Opprett og administrer rabattkuponger</p>
          </div>
          <div className="coupon-header-stats">
            <div className="coupon-stat-pill">
              <FiGift style={{ fontSize: '14px' }} />
              {coupons.length} Kuponger
            </div>
            <div className="coupon-stat-pill">
              <FiUsers style={{ fontSize: '14px' }} />
              {allTimeCouponsUsed} Brukt
            </div>
          </div>
        </div>

        {/* Create Coupon Card */}
        <div className="card coupon-create-card">
          <div className="coupon-create-header">
            <h2><FiPlus style={{ marginRight: '8px', verticalAlign: 'middle' }} />Opprett ny kupong</h2>
          </div>
          
          <form onSubmit={handleSubmit} className="coupon-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Kupongtittel</label>
                <input
                  id="title"
                  name="title"
                  placeholder="Skriv inn kupongtittel"
                  value={form.title}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="expiration">Utløpsdato</label>
                <input
                  id="expiration"
                  name="expiration"
                  type="date"
                  value={form.expiration}
                  onChange={handleChange}
                  className="form-input"
                  disabled={form.unlimited}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startTime">Fra tid (valgfri)</label>
                <input
                  id="startTime"
                  name="startTime"
                  type="time"
                  value={form.startTime}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="endTime">Til tid (valgfri)</label>
                <input
                  id="endTime"
                  name="endTime"
                  type="time"
                  value={form.endTime}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="maxUses">Maks antall bruk per bruker</label>
                <input
                  id="maxUses"
                  name="maxUses"
                  type="number"
                  min="1"
                  value={form.maxUses}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                <label className="checkbox-label" style={{ marginTop: '1.85rem' }}>
                  <input
                    type="checkbox"
                    name="repeatWeekly"
                    checked={form.repeatWeekly}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  Gjentas på faste ukedager
                </label>
              </div>
            </div>

            {form.repeatWeekly && (
              <div className="form-group">
                <label>Gjelder på disse dagene</label>
                <div className="weekday-chip-row">
                  {WEEKDAY_OPTIONS.map((day) => {
                    const selected = form.repeatDays.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        className="weekday-chip"
                        onClick={() => toggleRepeatDay(day.value)}
                        style={{
                          background: selected ? '#010f3c' : '#f1f5f9',
                          color: selected ? '#ffffff' : '#334155',
                          border: selected ? '1px solid #010f3c' : '1px solid #e2e8f0',
                        }}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="description">Beskrivelse</label>
              <input
                id="description"
                name="description"
                placeholder="Skriv inn kupongbeskrivelse"
                value={form.description}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="priceBadge">Pris-badge (valgfri)</label>
              <input
                id="priceBadge"
                name="priceBadge"
                placeholder="f.eks. Fra 99 kr"
                value={form.priceBadge}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="unlimited"
                checked={form.unlimited}
                onChange={handleChange}
                className="checkbox-input"
              />
              Ubegrenset bruk (ingen utløpsdato)
            </label>

            <button type="submit" className="btn btn-primary btn-create">
              <FiPlus style={{ fontSize: '18px' }} />
              Opprett kupong
            </button>
          </form>
        </div>

        {/* Stats Card */}
        <div className="card coupon-stats-card">
          <div className="coupon-stats-header">
            <h2><FiTrendingUp style={{ marginRight: '8px', verticalAlign: 'middle' }} />Bruksstatistikk</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span className="coupon-total-badge">{allTimeCouponsUsed} totalt brukt</span>
              <button className="refresh-btn" onClick={handleRefreshStats} style={{ background: '#16a34a', fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}>
                <FiRefreshCw style={{ marginRight: 0, fontSize: '0.75rem' }} />Oppdater
              </button>
            </div>
          </div>
          <div className="coupon-stats-grid">
            {couponStatsRows.length === 0 && (
              <div className="coupon-stat-item">
                <div className="coupon-stat-name">Ingen statistikk ennå</div>
                <div className="coupon-stat-value">0 brukt</div>
              </div>
            )}
            {couponStatsRows.map((row) => (
              <div key={row.id} className="coupon-stat-item">
                <div className="coupon-stat-name">
                  {row.title}
                  {row.isArchived && (
                    <span style={{ marginLeft: 8, fontSize: '0.72rem', color: '#94a3b8' }}>(slettet)</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div className="coupon-stat-value">{row.usedCount || 0} brukt</div>
                  {row.isArchived && (
                    <button
                      className="refresh-btn"
                      style={{ background: '#ef4444', fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}
                      onClick={async () => {
                        try {
                          // Coupons are only ever soft-deleted elsewhere - this is the
                          // one place that actually purges a coupon for good (cascades
                          // to its usage log and any grants via FK ON DELETE CASCADE).
                          const { error } = await supabase.from('coupons_catalog').delete().eq('id', row.couponId);
                          if (error) throw error;
                          await refreshStats(false);
                          toast.success('Statistikkrad slettet', { duration: 1800, position: 'top-center' });
                        } catch (error) {
                          toast.error('Kunne ikke slette statistikkrad', { duration: 2500, position: 'top-center' });
                        }
                      }}
                    >
                      Slett
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Coupons List */}
        <div className="card coupons-list-card">
          <div className="coupons-list-header">
            <h2><FiGift style={{ marginRight: '8px', verticalAlign: 'middle' }} />Aktive kuponger</h2>
            <span className="coupons-count">{coupons.length} kuponger</span>
          </div>

          <div className="coupons-list">
            {coupons.length === 0 && (
              <div className="no-coupons">Ingen kuponger opprettet enda.</div>
            )}

            {coupons.map((coupon) => (
              <div key={coupon.id} className="coupon-list-item">
                <div>
                  <div className="coupon-meta-line">
                    KUPONG · {coupon.unlimited || coupon.expiration === null ? 'UBEGRENSET' : 'AKTIV'}
                  </div>
                  <div className="coupon-list-title">{coupon.title}</div>
                  <div className="coupon-list-desc">{coupon.description}</div>
                  <div className="coupon-list-extra">
                    {coupon.unlimited || coupon.expiration === null
                      ? 'Ingen utløpsdato'
                      : coupon.expiration && coupon.expiration.trim() !== ''
                      ? `Utløper: ${formatDate(coupon.expiration)}`
                      : 'Ingen utløpsdato'}
                  </div>
                  <div className="coupon-list-extra">
                    Maks bruk per bruker: {Math.max(1, Number.parseInt(coupon.maxUses, 10) || 1)}
                  </div>
                  {coupon.priceBadge && (
                    <div className="coupon-list-extra">
                      Prisbadge: {coupon.priceBadge}
                    </div>
                  )}
                  {coupon.startTime && coupon.endTime && (
                    <div className="coupon-list-extra">
                      Tid: {coupon.startTime} til {coupon.endTime}
                    </div>
                  )}
                  {coupon.repeatWeekly && Array.isArray(coupon.repeatDays) && coupon.repeatDays.length > 0 && (
                    <div className="coupon-list-extra">
                      Gjelder: {coupon.repeatDays
                        .map((value) => WEEKDAY_OPTIONS.find((day) => day.value === value)?.label || value)
                        .join(', ')}
                    </div>
                  )}
                </div>
                <div className="coupon-item-actions">
                  <button
                    className="coupon-danger-btn"
                    onClick={async () => {
                      try {
                        const { error } = await supabase.from('coupons_catalog').update({ deleted: true }).eq('id', coupon.id);
                        if (error) throw error;
                        setCoupons(coupons.filter((c) => c.id !== coupon.id));
                        await refreshStats(false);
                        toast.success('Kupong slettet!', { duration: 2000, position: 'top-center' });
                      } catch (error) {
                        console.error('Error deleting coupon: ', error);
                        toast.error('Feil ved sletting av kupong', { duration: 3000, position: 'top-center' });
                      }
                    }}
                  >
                    <FiTrash2 style={{ marginRight: 6, verticalAlign: 'middle' }} />Slett
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </>
  );
}

export default NewCoupon;